import {ref} from "vue";
import type {App, Plugin} from "@vue/runtime-core";
import type {Axios} from "axios";
import {AuthMethod} from "@/model/auth-method";

export const Role = {
  TRAINEE: "TRAINEE",
  TRAINER: "TRAINER",
  ADMIN: "ADMIN",
  CONTENT_MANAGER: "CONTENT_MANAGER",
  SCHOOL_MANAGER: "SCHOOL_MANAGER",
  VIEWER: "VIEWER",
  DISABLED_USER: "DISABLED_USER",
  ALL: "ALL", // any authenticated user
};

const STORAGE_KEY_TOKEN = "internal-api-token";

export class Auth {
  authenticated = ref(false);
  token = "";
  payload = ref({
    acc: "",
    sub: "",
    fn: "",
    ln: "",
    roles: [] as string[],
    exp: 0,
    rvk: false,
    temp: false,
    missingContactData: false,
    authMethod: "",
  });
  error = null;
  onLogin: Function;
  onLogout: Function;
  axios: Axios;

  constructor(onLogin: Function, onLogout: Function, axios: Axios) {
    this.onLogin = onLogin;
    this.onLogout = onLogout;
    this.axios = axios;
  }

  isAuthenticated() {
    return this.authenticated.value;
  }

  async login(jwt: string) {
    localStorage.setItem(STORAGE_KEY_TOKEN, jwt);
    this.token = jwt;
    await this.loadCredentials();
    await this.onLogin();
  }

  async refreshToken() {
    const resp = await this.axios.post("/openid/token/refresh");
    localStorage.setItem(STORAGE_KEY_TOKEN, resp.data.token);
    this.token = resp.data.token;
    this.loadTokenData();
    this.authenticated.value = true;
  }

  async logout() {
    const authMethod = this.getAuthMethod();
    const token = this.token;

    this.clearContext();
    await this.onLogout(authMethod, token);
  }

  clearContext() {
    this.authenticated.value = false;
    this.token = "";
    this.payload.value = {
      acc: "",
      sub: "",
      fn: "",
      ln: "",
      roles: [] as string[],
      exp: 0,
      rvk: false,
      temp: false,
      missingContactData: false,
      authMethod: "",
    };
    localStorage.removeItem(STORAGE_KEY_TOKEN);
  }

  isTokenValid() {
    return this.isTokenValidAt(Date.now());
  }

  isTokenValidAt(time: number) {
    return (time / 1000) < this.payload.value.exp;
  }

  async loadCredentials() {
    if (this.isAuthenticated()) {
      return;
    }

    try {
      this.token = this.getToken();
      this.loadTokenData();

      if (this.isTokenValid()) {
        this.authenticated.value = true;
        return;
      }

      if (this.getAuthMethod() === AuthMethod.UAA) {
        await this.refreshToken();
        return;
      }

      this.clearContext();
    } catch {
      this.clearContext();
    }
  }

  getToken() {
    if (this.token && this.token.length > 0) {
      return this.token;
    }

    return localStorage.getItem(STORAGE_KEY_TOKEN) ?? "";
  }

  loadTokenData() {
    const parts = this.getToken().split(".");
    if (parts.length != 3) return;
    const base64 = parts[1].replaceAll("-", "+").replaceAll("_", "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.codePointAt(0)!.toString(16)).slice(-2);
        })
        .join("")
    );
    const obj = JSON.parse(json);
    this.payload.value = {
      acc: obj.acc,
      sub: obj.sub,
      fn: obj.fn,
      ln: obj.ln,
      exp: obj.exp,
      roles: obj.roles,
      rvk: obj.rvk,
      temp: obj.temp,
      missingContactData: obj.missingContactData,
      authMethod: obj.authMethod,
    };
  }

  getRoles() {
    return this.payload.value.roles;
  }

  getRole() {
    const roles = this.getRoles();
    return roles && roles.length > 0 ? roles[0] : null;
  }

  hasRole(role: string) {
    return this.isAuthenticated() && this.getRoles().includes(role);
  }

  hasSomeRole(...roles: string[]) {
    return this.isAuthenticated() && roles.some((r) => this.hasRole(r));
  }

  canSeeArchivedAccounts() {
    return this.hasSomeRole(Role.ADMIN, Role.VIEWER);
  }

  getRVK() {
    return this.payload.value.rvk;
  }

  getTemp() {
    return this.payload.value.temp;
  }

  getAcc() {
    return this.payload.value.acc;
  }

  getSsn() {
    return this.payload.value.sub;
  }

  getAuthMethod() {
    return this.payload.value.authMethod;
  }

  isMissingContactData() {
    return this.payload.value.missingContactData;
  }

  getDisplayName() {
    if (this.payload.value.fn && this.payload.value.ln) {
      return `${this.payload.value.fn} ${this.payload.value.ln}`;
    }

    if (this.payload.value.fn) {
      return `${this.payload.value.fn}`;
    }

    if (this.payload.value.ln) {
      return `${this.payload.value.ln}`;
    }

    return this.payload.value.sub;
  }
}

interface PluginOptions {
  onLogin: () => Promise<void>;
  onLogout: (authMethod: AuthMethod, token: string) => void;
  axios: Axios;
}

export const OAuthPlugin: Plugin = {
  install(app: App, options: PluginOptions) {
    const auth = new Auth(options.onLogin, options.onLogout, options.axios);
    app.provide("auth", auth);
    app.config.globalProperties.$auth = auth;
    app.config.globalProperties.$roles = Role;
  }
};
